import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import LockClockIcon from "@mui/icons-material/LockClock";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useForm, SubmitHandler } from "react-hook-form";
import {
  restPassword,
  authenticatePasswordResetToken,
} from "../../redux/actions/auth.actions";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import { useNavigate } from "react-router";
import IconButton from "@mui/material/IconButton";
import FlashMessage from "../../components/Flashmessage";
import Footer from "../../Template/footer";
import { ReCaptcha } from "../../components/Captcha";
import { object, string, TypeOf } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams } from "react-router";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";

const formSchema = object({
  password: string()
    .nonempty("Password is required.")
    .min(8, "Password must be at least 8 characters long.")
    .max(32, "Password must be less than 32 characters.")
    .regex(
      new RegExp(".*[A-Z].*"),
      "Password must have at least one uppercase character."
    )
    .regex(
      new RegExp(".*[a-z].*"),
      "Password must have at least one lowercase character."
    )
    .regex(
      new RegExp(".*[`~<>?,./!@#$%^&*()\\-_+=\"'|{}\\[\\];:\\\\].*"),
      "Password must have at least one special character."
    )
    .regex(new RegExp(".*\\d.*"), "Password must have at least one number."),
  token: string().nonempty("Token is required"),

  captchaToken: string().nonempty("Invalid token"),
});
type FormInput = TypeOf<typeof formSchema>;

const theme = createTheme();

const Resetpassword: React.FunctionComponent = () => {
  const dispatch = useDispatch();
  const { token } = useParams();
  const auth = useSelector((state: any) => state.auth);
  const ui = useSelector((state: any) => state.UI);

  const [values, setValues] = React.useState<any>({
    password: "",
    showPassword: false,
  });

  const {
    register,
    setValue,
    formState: { errors },
    handleSubmit,
  } = useForm<FormInput>({
    mode: "onChange",
    resolver: zodResolver(formSchema),
  });

  const navigate = useNavigate();

  useEffect(() => {
    dispatch<any>(authenticatePasswordResetToken(token, navigate));
    register("captchaToken", { required: true });
  }, [dispatch, register, token, navigate]);

  const onSubmit: SubmitHandler<FormInput> = (data) => {
    dispatch<any>(restPassword(data, navigate));
  };

  const onVerifyCaptcha = (token: string) => {
    setValue("captchaToken", token);
  };

  const handleClickShowPassword = () => {
    setValues({
      ...values,
      showPassword: !values.showPassword,
    });
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleChange =
    (prop: keyof FormInput) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setValues({ ...values, [prop]: event.target.value });
    };

  if (auth.authenticated) {
    //return  <Navigate to="/"/>
  }
  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
            <LockClockIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Reset your password
          </Typography>
          <FlashMessage />
          {auth.status && (
            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              sx={{ mt: 1 }}
            >
              <Typography variant="body2" gutterBottom>
                Your password must be have at least 8 characters long, 1
                uppercase, 1 lowercase, 1 number and 1 special character.
              </Typography>
              <TextField
                id="token"
                type="hidden"
                sx={{ display: "none" }}
                value={token}
                {...register("token")}
              />
              <FormControl
                sx={{ mt: 2, mb: 2, width: "45ch" }}
                variant="outlined"
              >
                <InputLabel htmlFor="outlined-adornment-password">
                  New Password *
                </InputLabel>
                <OutlinedInput
                  {...register("password")}
                  error={!!errors["password"]}
                  required
                  fullWidth
                  id="outlined-adornment-password"
                  type={values.showPassword ? "text" : "password"}
                  value={values.password}
                  onChange={handleChange("password")}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {values.showPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="New Password"
                />
                {errors["password"] && (
                  <FormHelperText error id="accountId-error">
                    {errors["password"].message}
                  </FormHelperText>
                )}
              </FormControl>
              <ReCaptcha onVerifyCaptcha={onVerifyCaptcha} />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={ui.loading}
              >
                {ui.loading ? "Sending..." : "Reset Password"}
              </Button>
              <Grid container>
                <Grid item xs>
                  <Link component={RouterLink} to="/login" variant="body2">
                    Sign In
                  </Link>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
        <Footer />
      </Container>
    </ThemeProvider>
  );
};
export default Resetpassword;
